import { PasswordInput as MantinePasswordInput, PasswordInputProps, Progress, Text, useMantineTheme } from '@mantine/core';
import { forwardRef, useState } from 'react';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';
import { defaultColorScheme } from '../../../theme/theme';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedPasswordInputProps extends PasswordInputProps {
  gradientFocus?: boolean;
  gradientVariant?: GradientVariant;
  showStrength?: boolean;
  useColorScheme?: boolean; // Use color scheme from state management
}

const getStrength = (password: string): number => {
  let strength = 0;
  if (password.length >= 8) strength += 25;
  if (password.length >= 12) strength += 15;
  if (/[a-z]/.test(password)) strength += 15;
  if (/[A-Z]/.test(password)) strength += 15;
  if (/[0-9]/.test(password)) strength += 15;
  if (/[^a-zA-Z0-9]/.test(password)) strength += 15;
  return Math.min(strength, 100);
};

const getStrengthLabel = (strength: number): string => {
  if (strength < 30) return 'Weak';
  if (strength < 60) return 'Fair';
  if (strength < 80) return 'Good';
  return 'Strong';
};

const getStrengthColor = (strength: number): string => {
  if (strength < 30) return 'red';
  if (strength < 60) return 'yellow';
  if (strength < 80) return 'blue';
  return 'green';
};

const PasswordInput = forwardRef<HTMLInputElement, ThemedPasswordInputProps>(
  (
    { gradientFocus = true, gradientVariant = 'primary', showStrength = false, useColorScheme = true, value, onChange, ...rest },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey);
    const reducedMotion = usePreferencesStore((state) => state.reducedMotion);
    const strongFocus = usePreferencesStore((state) => state.strongFocus);
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Determine which color to use
    let color = theme.other.gradients[gradientVariant].from;

    if (useColorScheme) {
      const effectiveColorSchemeKey = colorSchemeKey || defaultColorScheme.key;
      const colorScheme = theme.other.colorSchemes?.find((cs: any) => cs.key === effectiveColorSchemeKey);
      if (colorScheme) {
        color = colorScheme.primary.from;
      }
    }

    const [internalValue, setInternalValue] = useState('');

    const currentValue = value !== undefined ? value.toString() : internalValue;
    const strength = showStrength ? getStrength(currentValue) : 0;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (value === undefined) {
        setInternalValue(newValue);
      }
      onChange?.(e);
    };

    return (
      <>
        <MantinePasswordInput
          ref={ref}
          value={value}
          onChange={handleChange}
          aria-label="Password input"
          styles={{
            label: {
              textAlign: 'left !important' as any,
            },
            input: {
              paddingRight: '40px',
              transition: gradientFocus && !reducedMotion ? 'border-color 0.2s ease, box-shadow 0.2s ease' : 'none',
            },
          }}
          // Use Mantine's proper styles API for pseudo-selectors
          sx={
            gradientFocus
              ? (theme) => ({
                  input: {
                    '&:focus': {
                      borderColor: `${color} !important`,
                      boxShadow: strongFocus
                        ? `0 0 0 3px ${color}50 !important`
                        : `0 0 0 2px ${color}30 !important`,
                      outline: strongFocus ? `2px solid ${color} !important` : 'none',
                      outlineOffset: strongFocus ? '2px' : '0',
                    },
                    '&:focus-within': {
                      borderColor: `${color} !important`,
                    },
                  },
                })
              : undefined
          }
          {...rest}
        />
        {showStrength && currentValue && (
          <>
            <Progress
              value={strength}
              color={getStrengthColor(strength)}
              size="xs"
              mt={4}
              aria-label={`Password strength: ${getStrengthLabel(strength)}`}
            />
            <Text size="xs" c={getStrengthColor(strength)} mt={2} role="status" aria-live="polite" style={accessibilityStyles}>
              Password strength: {getStrengthLabel(strength)}
            </Text>
          </>
        )}
      </>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;
